/* eslint-disable max-len */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import uuid from 'react-uuid';
import {
  IS_ACTIVE,
  VALUATIONS_DEFAULT_CURRENCY,
  VALUATIONS_DEFAULT_CURRENCY_SYMBOL,
} from 'common/constants/valuations';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { ExtendedFabButton } from 'components/Buttons';
import {
  FUND_INVESTMENTS_ADD_COMPARABLE_SLUG,
  FUND_INVESTMENTS_ADD_COMPARABLE_TITLE,
  FUND_INVESTMENTS_COMPARABLES_TITLE,
  FUND_INVESTMENTS_COMPARABLES_TITLE_SINGULAR,
  FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_TITLE,
  FUND_INVESTMENTS_TITLE,
} from 'pages/FundInvestments/common/constants';
import { Search } from 'pages/Valuations/approaches/guidelinePublicCompanies/AddGpcDialog/components';
import {
  CompGroupsOptions,
  CompGroupsTableData,
} from 'pages/Valuations/components/CompGroups/components/CompGroupsOptions';
import { PUBLIC_COMPANIES, USE_CAP_IQ_ID, VALUATION, WAS_NOT_FOUND_MD } from 'pages/Valuations/util/constants';
import { CapIQCompanyData, PublicComp } from 'pages/ValuationsAllocation/types';
import { getGPCTableRows } from 'pages/ValuationsAllocation/util';
import { GPCData, TableRowValue } from 'pages/ValuationsAllocation/util/getGPCTableRows/types';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useGetPublicCompList } from 'services/hooks';
import { useGetMeasurementDatesByFirmId } from 'services/hooks/firm';
import { useGetGpc } from 'services/hooks/valuation';
import { getObjectValue, getStringValue, worldCurrencies } from 'utilities';
import { dbShortDate } from 'utilities/dateFormats';
import {
  ADD_COMP,
  ADD_PUBLIC,
  COMPANIES,
  COMPANY,
  COMPANY_NOT_PUBLIC,
  COMPANY_PRESENT_IN_TABLE,
  COMPANY_TICKER,
  EBITDA,
  ENTERPRISE_VALUE_MILLIONS,
  MARKET_CAP_MILLIONS,
  RETRIEVING_COMPANY,
  REVENUE,
  SOME_COMPANIES_NOT_PUBLIC,
  STOCK_PRICE,
  SYMBOL_NOT_FOUND,
} from '../constants';
import {
  AddGpcDialogProps,
  IsCompanyPublicProps,
  UseGetGpcReturn,
  UseGetMeasurementDatesByFirmIdReturn,
  UseGetPublicCompListReturn,
} from './AddGpcDialog.props';
import AddGpcDialogActions from './AddGpcDialogActions';
import AddGpcDialogContent from './AddGpcDialogContent';
import AddGpcDialogTitle from './AddGpcDialogTitle';
import checkCompExistsInTable from './utils/checkCompExistsInTable';
import uniqueCompaniesData from './utils/uniqueCompaniesData';

// This function kicks in when the user is searching by company or ticker. Since we do not use
// the measurement date in the comp groups page, we need a mechanism to determine if the company
// is public or not. We do this by checking if the measurement date is between the first and last
// pricing dates of the company or if it is still active but has a last pricing date in the past.
const isCompanyPublic = ({ companyData, measurementDate }: IsCompanyPublicProps) => {
  // We subtract 2 weeks because the last pricing date only happen on work days in the USA
  // so if the date is a holiday or a weekend, then the date of the data is the previous workday.
  // Also, in some other trading markets, like China, the market can be closed for as long as about a week.
  // So, we can assume the company is public if it has a last_pricing_date within the last 2 weeks.

  const { gpc } = getObjectValue(companyData);
  const { first_pricing_date, last_pricing_date, security_active_status } = getObjectValue(gpc);
  const firstPricingDateValue = first_pricing_date?.value?.toString() ?? '';
  const lastPricingDateValue = last_pricing_date?.value?.toString() ?? '';
  const securityActiveStatusValue = (security_active_status?.value as string) ?? '';

  const firstPricingDate = dbShortDate(moment(firstPricingDateValue.replace('  ', ' '), 'MMM D YYYY hh:mmA', true));
  const lastPricingDate = dbShortDate(moment(lastPricingDateValue.replace('  ', ' '), 'MMM D YYYY hh:mmA', true));
  const isCompanyActive = securityActiveStatusValue === IS_ACTIVE || false;

  // Define date to use
  // measurementDate is used on valuation page and current date is used on comp groups page
  const date = measurementDate?.date ?? dbShortDate();

  const twoWeeksAgo = dbShortDate(moment().subtract(2, 'w'));

  // check which date will be used: defined date or 2 weeks ago
  const lastDateForComparison = moment(date).isBefore(twoWeeksAgo) ? date : twoWeeksAgo;

  const isBetweenFirstAndLastPricingDates = moment(lastDateForComparison).isBetween(firstPricingDate, lastPricingDate);
  const isAfterOrSameThanFirstPricingDate = moment(lastDateForComparison).isSameOrAfter(firstPricingDate);
  const isLowerOrSameThanLastingDate = moment(lastDateForComparison).isSameOrBefore(lastPricingDate);

  // The company is public when...
  return (
    // The measurement date is between the dates of its first and last pricing date
    isBetweenFirstAndLastPricingDates ||
    // Or if it is after the last pricing date and is active or last date for comparison
    // is before than last pricing date.
    (isAfterOrSameThanFirstPricingDate && (isCompanyActive || isLowerOrSameThanLastingDate))
  );
};

const StyledTypography = styled(Typography)({
  paddingBottom: '1em',
  borderBottom: '2px solid black',
  marginBottom: '1.5em',
});

const StyledTableCell = styled(TableCell)({
  width: '6rem',
});

const StyledChipNotFound = styled(Chip)(({ theme }) => ({
  color: theme.palette.error.main,
  borderColor: theme.palette.error.main,
  '& .MuiChip-deleteIcon': {
    color: theme.palette.error.main,
  },
}));

const StyledCompaniesContainer = styled(Box)(({ theme }) => ({
  borderRadius: 4,
  border: `1px solid ${theme.palette.grey[300]}`,
  minHeight: '8rem',
}));

const AddGpcDialog = ({
  currentAsOfDate: asOfDate,
  currentComps,
  currentMarketCurrency,
  currentMarketDate,
  currentMeasurementDate,
  currentSetAreThereChanges,
  deleteCompGroup,
  disabled,
  firmId,
  onAdd,
  openGpcDialog,
  saveCompGroups,
  setOpenGpcDialog,
  source,
  tableData: gpcTableData,
}: AddGpcDialogProps) => {
  const [open, setOpen] = useState(false);
  const [measurementDates, fetchMeasurementDates] =
    useGetMeasurementDatesByFirmId() as unknown as UseGetMeasurementDatesByFirmIdReturn;
  const recentMeasurementDates = measurementDates.toSorted(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const [companyData, fetchCompanyData, cleanCompanyData] = useGetGpc() as unknown as UseGetGpcReturn;
  const [tableData, setTableData] = useState<any>(companyData);
  const compGroupsRef = useRef<HTMLButtonElement>(null);
  const [openCompGroupsOptions, setOpenCompGroupsOptions] = useState(false);
  const [{ companyInfo, isShowLoadingProgress }] = useStore() as unknown as UseStoreValues;

  const {
    marketCurrency: contextMarketCurrency,
    marketDate: contextMarketDate,
    measurementDate: contextMeasurementDate,
    setAreThereChanges: contextSetAreThereChanges,
  } = useContext(ValuationContext);

  const marketCurrency = currentMarketCurrency ?? contextMarketCurrency ?? null;
  const marketDate = currentMarketDate ?? contextMarketDate ?? null;
  const measurementDate = currentMeasurementDate ?? contextMeasurementDate ?? null;
  const setAreThereChanges = currentSetAreThereChanges ?? contextSetAreThereChanges ?? null;

  const [publicCompsData, fetchPCFromCompGroup, removeCompsData] =
    useGetPublicCompList() as unknown as UseGetPublicCompListReturn;
  const [multipleTickerSymbols, setMultipleTickerSymbols] = useState<string[]>([]);
  const [companiesData, setCompaniesData] = useState<CapIQCompanyData[] | null>(null);
  const [companyWasntPublic, setCompanyWasntPublic] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(COMPANY_TICKER);
  const [searchResultAlreadyPresent, setSearchResultAlreadyPresent] = useState<boolean>(false);
  const isSearchingBySingleCompany = searchCriteria === COMPANY_TICKER;

  const { financials_currency: financialCurrency } = getObjectValue(companyInfo);

  const companiesDataKeys = useMemo(() => (companiesData ? companiesData.map(() => uuid()) : []), [companiesData]);

  const isFundInvestment = useMemo(() => source === FUND_INVESTMENTS_TITLE, [source]);

  const lastCompanyDataIndex = useMemo(() => {
    if (isNil(companyData) || isEmpty(companyData)) {
      return -1;
    }

    return companyData.length - 1;
  }, [companyData]);

  const lastCompanyData = useMemo(() => {
    if (lastCompanyDataIndex < 0) {
      return null;
    }

    return companyData?.[lastCompanyDataIndex];
  }, [companyData, lastCompanyDataIndex]);

  const { financialsPeriods } = useContext(ValuationContext);

  const currencyCode = useMemo(
    () => currentMarketCurrency ?? financialCurrency ?? VALUATIONS_DEFAULT_CURRENCY,
    [currentMarketCurrency, financialCurrency]
  );

  const currencySymbol = useMemo(
    () =>
      worldCurrencies[(currentMarketCurrency ?? financialCurrency) as keyof typeof worldCurrencies] ??
      VALUATIONS_DEFAULT_CURRENCY_SYMBOL,
    [currentMarketCurrency, financialCurrency]
  );

  const initialValues = {
    companyName: RETRIEVING_COMPANY,
    tableRows: [
      { label: STOCK_PRICE, value: '' },
      {
        label: ENTERPRISE_VALUE_MILLIONS({
          currencyCode,
          currencySymbol,
        }),
        value: '',
      },
      {
        label: MARKET_CAP_MILLIONS({
          currencyCode,
          currencySymbol,
        }),
        value: '',
      },
      { label: REVENUE, value: '' },
      { label: EBITDA, value: '' },
    ],
  };

  const getCompanyError = (queriedCompany: string) => (
    <>
      <Typography variant="h3" align="center">{`'${queriedCompany}' ${WAS_NOT_FOUND_MD}`}</Typography>
      <Typography variant="subtitle2" align="justify">
        {USE_CAP_IQ_ID}
        <Typography variant="body1" component="span">
          IQ123456789
        </Typography>
      </Typography>
    </>
  );

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setTableData(null);
    setOpen(false);
    removeCompsData();
    cleanCompanyData();
    setMultipleTickerSymbols([]);
    if (setOpenGpcDialog) {
      setOpenGpcDialog(false);
    }
  }, [removeCompsData, cleanCompanyData, setOpenGpcDialog]);

  const handleToggle = () => {
    setOpenCompGroupsOptions(prevOpen => !prevOpen);
  };

  useEffect(() => {
    fetchMeasurementDates(firmId!);
  }, [fetchMeasurementDates, firmId]);

  useEffect(() => {
    if (isNil(companyData) || isNil(lastCompanyData)) {
      return;
    }

    if (companyData.every(comp => comp.valid_gpc_results)) {
      const compNotPresentInTable = checkCompExistsInTable({
        existingComps: currentComps,
        publicCompsData: {
          results: companyData.map(comp => comp?.gpc as PublicComp).filter(Boolean),
        },
      });

      const result = compNotPresentInTable ? compNotPresentInTable[lastCompanyDataIndex].valid_gpc_results : false;
      setSearchResultAlreadyPresent(!result);

      const isPublic = isCompanyPublic({ companyData: lastCompanyData, measurementDate });
      setCompanyWasntPublic(!isPublic);
      if (!isPublic) {
        setTableData({
          companyName: lastCompanyData.gpc?.company_name?.value ?? '',
          measurement_date: measurementDate,
        });
      } else {
        setTableData(
          getGPCTableRows({ gpcData: lastCompanyData.gpc as unknown as GPCData, financialsCurrency: currencyCode })
        );
      }
    } else {
      setTableData({
        error: getCompanyError(lastCompanyData?.requested_identifier),
      });
    }
  }, [companyData, currencyCode, currentComps, lastCompanyData, lastCompanyDataIndex, measurementDate]);

  useEffect(() => {
    if (publicCompsData) {
      const tmpCompaniesData: CapIQCompanyData[] =
        checkCompExistsInTable({
          existingComps: currentComps,
          publicCompsData,
        }) ?? [];

      const tickersInResponse = tmpCompaniesData.map(company => {
        const tickerSymbol = company.gpc?.ticker_symbol?.value as string;
        const ticker = tickerSymbol.toUpperCase();
        return {
          ticker,
        };
      });

      // These are the tickers from the search box
      multipleTickerSymbols.forEach((ticker: string) => {
        if (!tickersInResponse.some(t => t.ticker.includes(ticker.toUpperCase()))) {
          tmpCompaniesData.push({
            gpc: {
              company_name: {
                value: `"${ticker}" ${SYMBOL_NOT_FOUND}`,
              },
            },
            valid_gpc_results: false,
            notFound: true,
            requested_identifier: ticker,
          });
        }
      });

      setCompaniesData(tmpCompaniesData);
    }

    return () => setCompaniesData(null);
  }, [publicCompsData, multipleTickerSymbols, measurementDate, gpcTableData, currentComps]);

  const handleAdd = () => {
    /*
        #1 - Add a single company (GPC approach and comp group tables)
        #2 - Add multiple companies (GPC approach table)
        #3 - Add multiple companies (Comp group table)
      */
    // The user can choose to search by multiple tickers but still enter only one
    if (companyData && multipleTickerSymbols.length <= 1) {
      // #1
      onAdd(companyData);
    } else if (companiesData) {
      const validCompsData = companiesData.filter(c => c.valid_gpc_results);

      if (companiesData && saveCompGroups) {
        // #2
        saveCompGroups(validCompsData, false, [], null);
      } else if (!isEmpty(companiesData)) {
        // #3
        onAdd([...validCompsData]);
      }
    }
    handleClose();
    if (setAreThereChanges) {
      setAreThereChanges(true);
    }
  };

  const handleDelete = (requestedIdentifier: string) => {
    const allowedCompanies = companiesData?.filter(comp => comp.requested_identifier !== requestedIdentifier) ?? [];
    setCompaniesData(allowedCompanies);

    // Clean up comps when the user has deleted all companies manually
    if (allowedCompanies.length === 0) {
      removeCompsData();
    }
  };

  const arrowIcon = useMemo(() => {
    if (openCompGroupsOptions) {
      return <ArrowDropUpIcon id="comp-groups-up" />;
    }
    return <ArrowDropDownIcon id="comp-groups-down" />;
  }, [openCompGroupsOptions]);

  const isAnyCompanyNotPublic = useMemo(() => {
    if (!measurementDate) {
      return true;
    }
    return (publicCompsData?.results?.length ?? 0) < multipleTickerSymbols.length;
  }, [measurementDate, publicCompsData, multipleTickerSymbols]);

  const isSelectedCompanyNotPublic = useMemo(
    () => lastCompanyData && !isCompanyPublic({ companyData: lastCompanyData, measurementDate }),
    [lastCompanyData, measurementDate]
  );

  const shouldDisableAddBtn = useMemo(() => {
    const isCompanyEmptyOrNotPublic =
      isSearchingBySingleCompany && (isEmpty(companyData) || isSelectedCompanyNotPublic);

    const areCompaniesEmpty =
      !isSearchingBySingleCompany && (companiesData?.every(c => !c.valid_gpc_results) || isEmpty(companiesData));

    return isShowLoadingProgress || isCompanyEmptyOrNotPublic || areCompaniesEmpty || searchResultAlreadyPresent;
  }, [
    isSearchingBySingleCompany,
    companyData,
    isSelectedCompanyNotPublic,
    companiesData,
    isShowLoadingProgress,
    searchResultAlreadyPresent,
  ]);

  const triggerButton = useMemo(() => {
    switch (source) {
      case VALUATION:
        return (
          <>
            <ButtonGroup
              color="primary"
              variant="outlined"
              aria-label="split button"
              style={{
                borderRadius: '2.688rem',
                marginTop: '1.5em',
              }}>
              <Button
                id="add-comparable-company-btn-1"
                onClick={handleClickOpen}
                disabled={disabled}
                style={{
                  borderBottomLeftRadius: '2.688rem',
                  borderTopLeftRadius: '2.688rem',
                }}>
                {ADD_COMP}
              </Button>
              <Button
                ref={compGroupsRef}
                size="small"
                aria-controls={openCompGroupsOptions ? 'split-button-menu' : undefined}
                aria-expanded={openCompGroupsOptions ? 'true' : undefined}
                aria-label="select comp groups options"
                aria-haspopup="menu"
                disabled={disabled}
                onClick={handleToggle}
                style={{
                  borderBottomRightRadius: '2.688rem',
                  borderTopRightRadius: '2.688rem',
                }}>
                {arrowIcon}
              </Button>
            </ButtonGroup>
            <CompGroupsOptions
              setOpenCompGroupsOptions={setOpenCompGroupsOptions}
              compGroupsRef={compGroupsRef}
              openCompGroupsOptions={openCompGroupsOptions}
              saveCompGroups={saveCompGroups}
              deleteCompGroup={deleteCompGroup}
              tableData={gpcTableData as CompGroupsTableData}
              approachType={PUBLIC_COMPANIES}
            />
          </>
        );

      case FUND_INVESTMENTS_TITLE:
        return (
          <ExtendedFabButton
            addBottomMarginForDrawer
            disabled={disabled}
            id={FUND_INVESTMENTS_ADD_COMPARABLE_SLUG}
            label={FUND_INVESTMENTS_ADD_COMPARABLE_TITLE}
            onClick={handleClickOpen}
          />
        );

      default:
        return (
          <Button
            id="add-comparable-company-btn-2"
            variant="outlined"
            color="primary"
            endIcon={<AddIcon />}
            onClick={handleClickOpen}
            disabled={disabled}>
            {ADD_COMP}
          </Button>
        );
    }
  }, [arrowIcon, deleteCompGroup, disabled, gpcTableData, openCompGroupsOptions, saveCompGroups, source]);

  const addEntityLabel = useMemo(() => {
    const useSingularWord = uniqueCompaniesData(companiesData).length <= 1;

    let label = useSingularWord ? COMPANY : COMPANIES;

    if (isFundInvestment) {
      label = useSingularWord ? FUND_INVESTMENTS_COMPARABLES_TITLE_SINGULAR : FUND_INVESTMENTS_COMPARABLES_TITLE;
    }

    return `Add ${label}`;
  }, [companiesData, isFundInvestment]);

  return (
    <div style={{ 
      position: 'relative'
    }}>
      {triggerButton}

      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        fullWidth
        maxWidth="sm"
        data-testid="addGpcDialog"
        open={open || openGpcDialog}>
        <AddGpcDialogTitle id="customized-dialog-title" onClose={handleClose}>
          {isFundInvestment ? FUND_INVESTMENTS_PUBLIC_COMPS_DIALOG_TITLE : ADD_PUBLIC}
        </AddGpcDialogTitle>
        <Search
          asOfDate={asOfDate}
          fetchCompanyData={fetchCompanyData}
          fetchPCFromCompGroup={fetchPCFromCompGroup}
          financialsPeriods={financialsPeriods}
          initialValues={initialValues}
          isFundInvestment={isFundInvestment}
          marketCurrency={getStringValue(marketCurrency)}
          marketDate={getStringValue(marketDate)}
          measurementDate={measurementDate || recentMeasurementDates[0] || {}}
          multipleTickerSymbols={multipleTickerSymbols}
          removeCompsData={removeCompsData}
          searchCriteria={searchCriteria}
          setMultipleTickerSymbols={setMultipleTickerSymbols}
          setSearchCriteria={setSearchCriteria}
          setTableData={setTableData}
        />
        {
          // If the user searches by company or ticker
          isSearchingBySingleCompany ? (
            <>
              {tableData?.companyName && (
                <AddGpcDialogContent data-testid="company-info" dividers>
                  <Typography variant="h2">{tableData.companyName}</Typography>
                  <StyledTypography>{tableData.ticker}</StyledTypography>
                  {companyWasntPublic || searchResultAlreadyPresent ? (
                    <Typography>
                      {companyWasntPublic &&
                        COMPANY_NOT_PUBLIC({
                          company_name: String(lastCompanyData?.gpc?.company_name?.value ?? ''),
                          measurement_date: tableData?.measurement_date,
                        })}
                      {searchResultAlreadyPresent &&
                        COMPANY_PRESENT_IN_TABLE({
                          company_name: String(lastCompanyData?.gpc?.company_name?.value ?? ''),
                        })}
                    </Typography>
                  ) : (
                    <Table aria-label="Guideline Public Company Table" size="small">
                      <TableBody>
                        {tableData.tableRows.map((tableRow: TableRowValue, key: string) => (
                          <TableRow key={`${tableRow.label}-${tableRow.value}`}>
                            <StyledTableCell align="right">
                              <strong data-testid={`company-data-row-value-${key}`}>{tableRow.value}</strong>
                            </StyledTableCell>
                            <TableCell align="left">{tableRow.label}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </AddGpcDialogContent>
              )}
              {tableData?.error && (
                <AddGpcDialogContent data-testid="company-info">{tableData.error}</AddGpcDialogContent>
              )}
            </>
          ) : (
            // If the user search by Enter Multiple Tickers
            <>
              {companiesData && (
                <div style={{ padding: '1rem' }}>
                  <StyledCompaniesContainer width="100%">
                    <div style={{ padding: '1rem' }}>
                      {uniqueCompaniesData(companiesData)?.map((comp, compIndex) => {
                        const ChipComponent = comp.valid_gpc_results ? Chip : StyledChipNotFound;
                        return (
                          <ChipComponent
                            key={companiesDataKeys[compIndex]}
                            variant="outlined"
                            label={String(comp.gpc?.company_name?.value ?? '')}
                            size="small"
                            color={comp.notPublic ? 'default' : 'primary'}
                            deleteIcon={<ClearIcon />}
                            onDelete={() => handleDelete(comp.requested_identifier)}
                            style={{
                              marginRight: '1rem',
                              marginBottom: '1rem',
                            }}
                          />
                        );
                      })}
                    </div>
                  </StyledCompaniesContainer>
                  {isAnyCompanyNotPublic && (
                    <Typography variant="subtitle2">
                      {SOME_COMPANIES_NOT_PUBLIC(measurementDate?.date ?? '')}
                    </Typography>
                  )}
                </div>
              )}
            </>
          )
        }
        <AddGpcDialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            data-testid="add-gpc-company-btn"
            disabled={shouldDisableAddBtn}
            variant="contained"
            color="secondary">
            {addEntityLabel}
          </Button>
        </AddGpcDialogActions>
      </Dialog>
    </div>
  );
};

export default AddGpcDialog;
